import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface CompanyRegisterProvider {
  id: string;
  name: string;
  countryCode: string;
  search: (query: string) => Promise<CompanySearchResult[]>;
  getCompanyDetails: (id: string) => Promise<CompanyDetails | null>;
  requiresApiKey: boolean;
}

export interface CompanySearchResult {
  id: string;
  name: string;
  organizationNumber?: string;
  address?: string;
  source: string;
}

export interface CompanyDetails {
  id: string;
  name: string;
  organizationNumber?: string;
  address?: {
    street?: string;
    postalCode?: string;
    city?: string;
    country?: string;
  };
  contactInfo?: {
    email?: string;
    phone?: string;
    website?: string;
  };
  industry?: string;
  registrationDate?: string;
  source: string;
}

export const brregProvider: CompanyRegisterProvider = {
  id: "brreg",
  name: "Brønnøysundregistrene (Norway)",
  countryCode: "NO",
  requiresApiKey: false,
  
  async search(query: string): Promise<CompanySearchResult[]> {
    try {
      if (!query || query.length < 2) return [];
      
      console.log("BRREG searching for:", query);
      
      let url: string;
      
      const isOrgNumber = /^\d+$/.test(query.trim());
      
      if (isOrgNumber) {
        url = `https://data.brreg.no/enhetsregisteret/api/enheter/${encodeURIComponent(query.trim())}`;
        
        console.log("BRREG API request:", url);
        
        const response = await fetch(url);
        
        if (response.status === 404) {
          console.log("BRREG: No results found");
          return [];
        }
        
        if (!response.ok) {
          throw new Error(`BRREG API error: ${response.status}`);
        }
        
        const data = await response.json();
        console.log("BRREG API response:", data);
        
        return [{
          id: data.organisasjonsnummer,
          name: data.navn,
          organizationNumber: data.organisasjonsnummer,
          address: data.forretningsadresse ? 
            `${data.forretningsadresse.adresse?.[0] || ''}, ${data.forretningsadresse.postnummer || ''} ${data.forretningsadresse.poststed || ''}` 
            : undefined,
          source: "BRREG"
        }];
      } else {
        url = `https://data.brreg.no/enhetsregisteret/api/enheter?navn=${encodeURIComponent(query)}&size=10`;
        
        console.log("BRREG API request:", url);
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`BRREG API error: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data._embedded?.enheter) {
          console.log("BRREG: No results found in response");
          return [];
        }
        
        console.log(`BRREG: Found ${data._embedded.enheter.length} results`);
        
        return data._embedded.enheter.map((item: any) => ({
          id: item.organisasjonsnummer,
          name: item.navn,
          organizationNumber: item.organisasjonsnummer,
          address: item.forretningsadresse ? 
            `${item.forretningsadresse.adresse?.[0] || ''}, ${item.forretningsadresse.postnummer || ''} ${item.forretningsadresse.poststed || ''}` 
            : undefined,
          source: "BRREG"
        }));
      }
    } catch (error) {
      console.error("BRREG search error:", error);
      toast.error("Error searching Norwegian company register");
      return [];
    }
  },
  
  async getCompanyDetails(id: string): Promise<CompanyDetails | null> {
    try {
      const url = `https://data.brreg.no/enhetsregisteret/api/enheter/${id}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`BRREG API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      return {
        id: data.organisasjonsnummer,
        name: data.navn,
        organizationNumber: data.organisasjonsnummer,
        address: data.forretningsadresse ? {
          street: data.forretningsadresse.adresse?.[0] || undefined,
          postalCode: data.forretningsadresse.postnummer || undefined,
          city: data.forretningsadresse.poststed || undefined,
          country: "Norway"
        } : undefined,
        contactInfo: {
          email: data.epost || undefined,
          phone: data.telefon || undefined,
          website: data.hjemmeside || undefined
        },
        industry: data.naeringskode1?.beskrivelse || undefined,
        registrationDate: data.registreringsdatoEnhetsregisteret,
        source: "BRREG"
      };
    } catch (error) {
      console.error("BRREG details error:", error);
      toast.error("Error fetching Norwegian company details");
      return null;
    }
  }
};

export const bolagsverketProvider: CompanyRegisterProvider = {
  id: "bolagsverket",
  name: "Bolagsverket (Sweden)",
  countryCode: "SE",
  requiresApiKey: true,
  
  async search(query: string): Promise<CompanySearchResult[]> {
    try {
      if (!query || query.length < 2) return [];
      
      const userPrefs = await getUserCompanyRegisterPreferences();
      const apiKey = userPrefs?.bolagsverket_api_key;
      
      if (!apiKey) {
        toast.error("Bolagsverket API key not configured in profile settings");
        return [];
      }
      
      const isOrgNumber = /^[\d-]+$/.test(query.trim());
      
      const cleanQuery = isOrgNumber ? query.replace(/-/g, '') : query;
      
      const baseUrl = "https://api.bolagsverket.se/v1/companies";
      const searchUrl = isOrgNumber 
        ? `${baseUrl}/${cleanQuery}` 
        : `${baseUrl}/search?name=${encodeURIComponent(cleanQuery)}&limit=10`;
      
      console.log(`Would fetch from Bolagsverket API: ${searchUrl}`);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (isOrgNumber) {
        if (cleanQuery === "5560127375" || cleanQuery === "556012-7375") {
          return [{
            id: "5560127375",
            name: "Volvo AB",
            organizationNumber: "556012-7375",
            address: "405 08 Gothenburg, Sweden",
            source: "BOLAGSVERKET"
          }];
        } else if (cleanQuery === "5563259431" || cleanQuery === "556325-9431") {
          return [{
            id: "5563259431",
            name: "IKEA AB",
            organizationNumber: "556325-9431",
            address: "Box 702, 343 81 Älmhult, Sweden",
            source: "BOLAGSVERKET"
          }];
        }
      } else {
        if (query.toLowerCase().includes("volvo")) {
          return [
            {
              id: "5560127375",
              name: "Volvo AB",
              organizationNumber: "556012-7375",
              address: "405 08 Gothenburg, Sweden",
              source: "BOLAGSVERKET"
            },
            {
              id: "5560893466",
              name: "Volvo Cars AB",
              organizationNumber: "556089-3466",
              address: "405 31 Gothenburg, Sweden",
              source: "BOLAGSVERKET"
            }
          ];
        } else if (query.toLowerCase().includes("ikea")) {
          return [
            {
              id: "5563259431",
              name: "IKEA AB",
              organizationNumber: "556325-9431",
              address: "Box 702, 343 81 Älmhult, Sweden",
              source: "BOLAGSVERKET"
            },
            {
              id: "5566747551",
              name: "IKEA of Sweden AB",
              organizationNumber: "556674-7551",
              address: "Box 702, 343 81 Älmhult, Sweden",
              source: "BOLAGSVERKET"
            }
          ];
        }
      }
      
      return [];
    } catch (error) {
      console.error("Bolagsverket search error:", error);
      toast.error("Error searching Swedish company register");
      return [];
    }
  },
  
  async getCompanyDetails(id: string): Promise<CompanyDetails | null> {
    try {
      const userPrefs = await getUserCompanyRegisterPreferences();
      const apiKey = userPrefs?.bolagsverket_api_key;
      
      if (!apiKey) {
        toast.error("Bolagsverket API key not configured in profile settings");
        return null;
      }
      
      console.log(`Would fetch details from Bolagsverket API for company ID: ${id}`);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (id === "5560127375") {
        return {
          id: "5560127375",
          name: "Volvo AB",
          organizationNumber: "556012-7375",
          address: {
            street: "Gropegårdsgatan 2",
            postalCode: "405 08",
            city: "Gothenburg",
            country: "Sweden"
          },
          contactInfo: {
            email: "info@volvo.com",
            phone: "+46 31 66 00 00",
            website: "https://www.volvogroup.com"
          },
          industry: "Motor Vehicle Manufacturing",
          registrationDate: "1927-05-25",
          source: "BOLAGSVERKET"
        };
      } else if (id === "5563259431") {
        return {
          id: "5563259431",
          name: "IKEA AB",
          organizationNumber: "556325-9431",
          address: {
            street: "Box 702",
            postalCode: "343 81",
            city: "Älmhult",
            country: "Sweden"
          },
          contactInfo: {
            email: "info@ikea.com",
            phone: "+46 476 810 00",
            website: "https://www.ikea.com"
          },
          industry: "Furniture Retail",
          registrationDate: "1943-10-28",
          source: "BOLAGSVERKET"
        };
      }
      
      return null;
    } catch (error) {
      console.error("Bolagsverket details error:", error);
      toast.error("Error fetching Swedish company details");
      return null;
    }
  }
};

export const companiesHouseProvider: CompanyRegisterProvider = {
  id: "companieshouse",
  name: "Companies House (UK)",
  countryCode: "GB",
  requiresApiKey: true,
  
  async search(query: string): Promise<CompanySearchResult[]> {
    try {
      if (!query || query.length < 2) return [];
      
      const userPrefs = await getUserCompanyRegisterPreferences();
      const apiKey = userPrefs?.companieshouse_api_key;
      
      if (!apiKey) {
        toast.error("Companies House API key not configured in profile settings");
        return [];
      }
      
      console.log(`Would fetch from Companies House API with query: ${query}`);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (query.toLowerCase().includes("tesco")) {
        return [
          {
            id: "00445790",
            name: "TESCO PLC",
            organizationNumber: "00445790",
            address: "Tesco House, Shire Park, Kestrel Way, Welwyn Garden City, Hertfordshire, AL7 1GA",
            source: "COMPANIESHOUSE"
          }
        ];
      } else if (query.toLowerCase().includes("sainsbury")) {
        return [
          {
            id: "00185647",
            name: "J SAINSBURY PLC",
            organizationNumber: "00185647",
            address: "33 Holborn, London, EC1N 2HT",
            source: "COMPANIESHOUSE"
          }
        ];
      } else if (query.toLowerCase().includes("barclays")) {
        return [
          {
            id: "00048839",
            name: "BARCLAYS PLC",
            organizationNumber: "00048839",
            address: "1 Churchill Place, London, E14 5HP",
            source: "COMPANIESHOUSE"
          },
          {
            id: "01026167",
            name: "BARCLAYS BANK PLC",
            organizationNumber: "01026167",
            address: "1 Churchill Place, London, E14 5HP",
            source: "COMPANIESHOUSE"
          }
        ];
      }
      
      return [];
    } catch (error) {
      console.error("Companies House search error:", error);
      toast.error("Error searching UK company register");
      return [];
    }
  },
  
  async getCompanyDetails(id: string): Promise<CompanyDetails | null> {
    try {
      const userPrefs = await getUserCompanyRegisterPreferences();
      const apiKey = userPrefs?.companieshouse_api_key;
      
      if (!apiKey) {
        toast.error("Companies House API key not configured in profile settings");
        return null;
      }
      
      console.log(`Would fetch details from Companies House API for company ID: ${id}`);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (id === "00445790") {
        return {
          id: "00445790",
          name: "TESCO PLC",
          organizationNumber: "00445790",
          address: {
            street: "Tesco House, Shire Park, Kestrel Way",
            postalCode: "AL7 1GA",
            city: "Welwyn Garden City",
            country: "United Kingdom"
          },
          contactInfo: {
            website: "https://www.tesco.com"
          },
          industry: "Retail",
          registrationDate: "1947-11-27",
          source: "COMPANIESHOUSE"
        };
      } else if (id === "00185647") {
        return {
          id: "00185647",
          name: "J SAINSBURY PLC",
          organizationNumber: "00185647",
          address: {
            street: "33 Holborn",
            postalCode: "EC1N 2HT",
            city: "London",
            country: "United Kingdom"
          },
          contactInfo: {
            website: "https://www.sainsburys.co.uk"
          },
          industry: "Retail",
          registrationDate: "1922-01-23",
          source: "COMPANIESHOUSE"
        };
      } else if (id === "00048839" || id === "01026167") {
        return {
          id: id,
          name: id === "00048839" ? "BARCLAYS PLC" : "BARCLAYS BANK PLC",
          organizationNumber: id,
          address: {
            street: "1 Churchill Place",
            postalCode: "E14 5HP",
            city: "London",
            country: "United Kingdom"
          },
          contactInfo: {
            website: "https://www.barclays.co.uk"
          },
          industry: "Financial Services",
          registrationDate: id === "00048839" ? "1896-06-25" : "1971-01-01",
          source: "COMPANIESHOUSE"
        };
      }
      
      return null;
    } catch (error) {
      console.error("Companies House details error:", error);
      toast.error("Error fetching UK company details");
      return null;
    }
  }
};

export const cvrProvider: CompanyRegisterProvider = {
  id: "cvr",
  name: "CVR (Denmark)",
  countryCode: "DK",
  requiresApiKey: false,
  
  async search(query: string): Promise<CompanySearchResult[]> {
    try {
      if (!query || query.length < 2) return [];
      
      console.log(`Would fetch from Danish CVR API with query: ${query}`);
      
      const isCvrNumber = /^\d{8}$/.test(query.trim());
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (isCvrNumber) {
        if (query.trim() === "25050053") {
          return [
            {
              id: "25050053",
              name: "Lego A/S",
              organizationNumber: "25050053",
              address: "Åstvej 1, 7190 Billund",
              source: "CVR"
            }
          ];
        } else if (query.trim() === "12345678") {
          return [
            {
              id: "12345678",
              name: "Danske Bank A/S",
              organizationNumber: "12345678",
              address: "Holmens Kanal 2-12, 1092 København K",
              source: "CVR"
            }
          ];
        }
      } else {
        if (query.toLowerCase().includes("lego")) {
          return [
            {
              id: "25050053",
              name: "Lego A/S",
              organizationNumber: "25050053",
              address: "Åstvej 1, 7190 Billund",
              source: "CVR"
            },
            {
              id: "36963085",
              name: "Lego Koncernen",
              organizationNumber: "36963085",
              address: "Koldingvej 2, 7190 Billund",
              source: "CVR"
            }
          ];
        } else if (query.toLowerCase().includes("danske")) {
          return [
            {
              id: "12345678",
              name: "Danske Bank A/S",
              organizationNumber: "12345678",
              address: "Holmens Kanal 2-12, 1092 København K",
              source: "CVR"
            }
          ];
        } else if (query.toLowerCase().includes("maersk")) {
          return [
            {
              id: "87654321",
              name: "A.P. Møller - Mærsk A/S",
              organizationNumber: "87654321",
              address: "Esplanaden 50, 1263 København K",
              source: "CVR"
            }
          ];
        }
      }
      
      return [];
    } catch (error) {
      console.error("CVR search error:", error);
      toast.error("Error searching Danish company register");
      return [];
    }
  },
  
  async getCompanyDetails(id: string): Promise<CompanyDetails | null> {
    try {
      console.log(`Would fetch details from Danish CVR API for company ID: ${id}`);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (id === "25050053" || id === "36963085") {
        return {
          id: id,
          name: id === "25050053" ? "Lego A/S" : "Lego Koncernen",
          organizationNumber: id,
          address: {
            street: id === "25050053" ? "Åstvej 1" : "Koldingvej 2",
            postalCode: "7190",
            city: "Billund",
            country: "Denmark"
          },
          contactInfo: {
            email: "service@lego.com",
            phone: "+45 79 50 60 70",
            website: "https://www.lego.com"
          },
          industry: "Manufacturing toys",
          registrationDate: "1932-08-10",
          source: "CVR"
        };
      } else if (id === "12345678") {
        return {
          id: "12345678",
          name: "Danske Bank A/S",
          organizationNumber: "12345678",
          address: {
            street: "Holmens Kanal 2-12",
            postalCode: "1092",
            city: "København K",
            country: "Denmark"
          },
          contactInfo: {
            phone: "+45 70 12 34 56",
            website: "https://www.danskebank.dk"
          },
          industry: "Banking",
          registrationDate: "1871-10-05",
          source: "CVR"
        };
      } else if (id === "87654321") {
        return {
          id: "87654321",
          name: "A.P. Møller - Mærsk A/S",
          organizationNumber: "87654321",
          address: {
            street: "Esplanaden 50",
            postalCode: "1263",
            city: "København K",
            country: "Denmark"
          },
          contactInfo: {
            phone: "+45 33 63 33 63",
            website: "https://www.maersk.com"
          },
          industry: "Shipping & Logistics",
          registrationDate: "1904-04-16",
          source: "CVR"
        };
      }
      
      return null;
    } catch (error) {
      console.error("CVR details error:", error);
      toast.error("Error fetching Danish company details");
      return null;
    }
  }
};

export async function getUserCompanyRegisterPreferences() {
  try {
    const { data: userResponse, error: userError } = await supabase.auth.getUser();
    if (userError) {
      console.error("Error getting current user:", userError);
      return null;
    }
    
    if (!userResponse.user) {
      return null;
    }
    
    const { data, error } = await supabase
      .from('user_preferences')
      .select('company_register_provider, bolagsverket_api_key, companieshouse_api_key')
      .eq('user_id', userResponse.user.id)
      .single();
    
    if (error && error.code !== 'PGRST116') {
      console.error("Error fetching company register preferences:", error);
      return null;
    }
    
    return data || null;
  } catch (error) {
    console.error("Error fetching company register preferences:", error);
    return null;
  }
}

export async function isProviderEnabled(providerId: string): Promise<boolean> {
  try {
    const settingKey = `enable_company_register_${providerId}`;
    console.log(`Checking if provider ${providerId} is enabled...`);
    
    const { data, error } = await supabase.rpc('fetch_public_setting', { setting_key: settingKey });
    
    if (error) {
      console.error(`Error checking if provider ${providerId} is enabled:`, error);
      return true; // Default to enabled if there's an error
    }
    
    const isEnabled = data === null || data === "true";
    console.log(`Provider ${providerId} is ${isEnabled ? 'enabled' : 'disabled'}`);
    return isEnabled;
  } catch (error) {
    console.error(`Error checking if provider ${providerId} is enabled:`, error);
    return true; // Default to enabled if there's an error
  }
}

export const companyRegisters: CompanyRegisterProvider[] = [
  brregProvider,
  bolagsverketProvider,
  companiesHouseProvider,
  cvrProvider,
];

export const getAvailableProviders = async (): Promise<CompanyRegisterProvider[]> => {
  const allProviders = companyRegisters;
  
  try {
    const enabledProviders: CompanyRegisterProvider[] = [];
    
    for (const provider of allProviders) {
      const isEnabled = await isProviderEnabled(provider.id);
      if (isEnabled) {
        enabledProviders.push(provider);
      }
    }
    
    return enabledProviders;
  } catch (error) {
    console.error("Error filtering available providers:", error);
    return allProviders;
  }
};

export const getProviderById = async (id: string): Promise<CompanyRegisterProvider | undefined> => {
  try {
    const isEnabled = await isProviderEnabled(id);
    if (!isEnabled) {
      return undefined;
    }
    
    return companyRegisters.find(provider => provider.id === id);
  } catch (error) {
    console.error(`Error getting provider ${id}:`, error);
    return companyRegisters.find(provider => provider.id === id);
  }
};
